import { CHART_COLORS } from '../constants'
import { ChartColorKeys } from 'components/charts/types.ts'
import { BillShock } from '@boltenergy-be/shared'

/** The intended pattern widths, in css px */
const STRIPE_WIDTH = 3
const WHITESPACE_WIDTH = 3

/** Tile size for seamless tiles based on STRIPE_WIDTH and WHITESPACE_WIDTH */
const STRIPE_TILE_SIZE = Math.max(2, Math.round((STRIPE_WIDTH + WHITESPACE_WIDTH) * Math.SQRT2))

/**
 * Half a stripe measured along the tile's side, in whole px so stripe edges land on pixel corners (rounds the stripe to
 * a multiple of √2 px, e.g. 2,83px for the intended 3px). Capped at half a tile, or the stripes would touch
 */
const STRIPE_HALF_FOOTPRINT = Math.min(Math.max(1, Math.round(STRIPE_WIDTH / Math.SQRT2)), Math.floor(STRIPE_TILE_SIZE / 2))

/**
 * Function to update proposed instalment to 20 if it's a negative amount
 *
 * @param {BillShock} billShockData
 * @returns {BillShock}
 */
export const updateProposedInstalmentBillShockData = (billShockData: BillShock): BillShock => {
  const futureMonths = billShockData.future.map((month) => {
    return {
      ...month,
      proposedInstalment: month.proposedInstalment < 20 ? 20 : month.proposedInstalment
    }
  })

  return {
    ...billShockData,
    future: futureMonths,
    instalment: {
      ...billShockData.instalment,
      minimum: billShockData.instalment.minimum < 20 ? 20 : billShockData.instalment.minimum,
      proposed: billShockData.instalment.proposed < 20 ? 20 : billShockData.instalment.proposed
    }
  }
}

/**
 * Get settlement color based on settlement total amount
 * @param {number} settlementTotal
 * @returns {string}
 */
export const getSettlementChartColor = (settlementTotal: number): string =>
  settlementTotal <= 0 ? CHART_COLORS.green : CHART_COLORS.orange

/**
 * Create a stripped canvas pattern with the given color, matching the stripped legend indicator: stripes running from
 * the bottom left to the top right, sized by STRIPE_WIDTH & WHITESPACE_WIDTH
 * @param {ChartColorKeys} color
 * @returns {CanvasPattern | string} the pattern, or the plain color when no canvas context is available
 */
export const getChartPattern = (color: ChartColorKeys): CanvasPattern | string => {
  /**
   * Chart.js paints in css pixels, so a tile of STRIPE_TILE_SIZE would be blown up on a high density screen. The tile is
   * drawn at the device resolution instead & scaled back down below, giving every tile pixel a single device pixel. Both
   * sizes stay whole pixels, leaving the browser nothing to resample between two repetitions of the tile
   */
  const canScalePattern = typeof DOMMatrix === 'function'
  const pixelRatio = canScalePattern ? Math.max(1, Math.round(window.devicePixelRatio || 1)) : 1
  const size = STRIPE_TILE_SIZE * pixelRatio

  const tile = document.createElement('canvas')
  tile.width = size
  tile.height = size

  const context = tile.getContext('2d')
  if (!context) return CHART_COLORS[color]

  context.fillStyle = CHART_COLORS.background
  context.fillRect(0, 0, size, size)
  context.strokeStyle = CHART_COLORS[color]
  // The footprint on the side of the tile is √2 times as long as the stripe it belongs to is wide
  context.lineWidth = STRIPE_HALF_FOOTPRINT * Math.SQRT2 * pixelRatio

  /**
   * The stripe across the tile, plus the two through its corners that the neighbouring tiles only show a part of. Every
   * stripe runs a tile past both ends of the tile, keeping its line caps out of the drawn area: a cap on a corner of the
   * tile would slice the stripe off perpendicularly, leaving a notch that repeats on every tile
   */
  for (const offset of [0, size, size * 2]) {
    context.beginPath()
    context.moveTo(offset - 2 * size, 2 * size)
    context.lineTo(offset + 2 * size, -2 * size)
    context.stroke()
  }

  const pattern = context.createPattern(tile, 'repeat')
  if (!pattern) return CHART_COLORS[color]

  if (pixelRatio > 1 && typeof pattern.setTransform === 'function') {
    pattern.setTransform(new DOMMatrix([1 / pixelRatio, 0, 0, 1 / pixelRatio, 0, 0]))
  }

  return pattern
}
